import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormGroup, Label, Input, FormFeedback, Row, Col, Spinner } from 'reactstrap';
import { X, Check } from 'react-feather';
import { useDispatch } from 'react-redux';
import debounce from 'lodash.debounce';
import { getBrandById } from '../../ReduxToolkit/Reducers/brands';

const getRowErrors = (errors, fieldPrefix, index) => {
  const keys = fieldPrefix.split('.');
  let node = errors;
  for (const key of keys) {
    node = node?.[key];
  }
  return node?.[index];
};

const BrandInSpaceRow = ({
  control,
  errors,
  index,
  onRemove,
  canRemove,
  rowMarginBottom = '20px',
  fieldPrefix = 'brand_in_space.brands',
  nameLabel = 'Brand Name',
  nameRequired = true,
  websiteRequired = true,
  namePlaceholder = 'Enter brand name',
  onWebsiteBlur,
  websiteFooter,
}) => {
  const dispatch = useDispatch();
  const { setValue, watch, clearErrors } = useFormContext();
  const [fetchState, setFetchState] = useState({ loading: false, error: null, loaded: false });
  const brandId = watch(`${fieldPrefix}.${index}.brand_id`);
  const brandName = watch(`${fieldPrefix}.${index}.name`);

  const fetchBrand = useCallback(async (id) => {
    const numericId = Number(id);

    if (!id || !Number.isInteger(numericId) || numericId <= 0) {
      setFetchState({ loading: false, error: null, loaded: false });
      return;
    }

    setFetchState({ loading: true, error: null, loaded: false });

    const result = await dispatch(getBrandById(numericId));

    if (result?.statusCode === 200 && result?.data) {
      setValue(`${fieldPrefix}.${index}.name`, result.data.name || '', { shouldValidate: true });
      setValue(`${fieldPrefix}.${index}.website`, result.data.website || '', { shouldValidate: true });
      clearErrors([
        `${fieldPrefix}.${index}.name`,
        `${fieldPrefix}.${index}.website`,
        `${fieldPrefix}.${index}.brand_id`,
      ]);
      setFetchState({ loading: false, error: null, loaded: true });
    } else {
      setFetchState({
        loading: false,
        error: result?.message || 'Brand not found',
        loaded: false,
      });
    }
  }, [dispatch, setValue, clearErrors, index, fieldPrefix]);

  useEffect(() => {
    const handler = debounce((id) => {
      fetchBrand(id);
    }, 500);

    handler(brandId);
    return () => handler.cancel();
  }, [brandId, fetchBrand]);

  const hasPopulatedBrand = fetchState.loaded && brandName;
  const rowErrors = getRowErrors(errors, fieldPrefix, index);

  return (
    <div style={{ marginBottom: rowMarginBottom }}>
      <Row>
        <Col md={3}>
          <FormGroup>
            <Label style={labelStyle}>
              Brand ID{' '}
              <span style={{ color: '#aaa', fontWeight: '400' }}>(optional)</span>
            </Label>
            <div style={{ position: 'relative' }}>
              <Controller
                name={`${fieldPrefix}.${index}.brand_id`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    min="1"
                    placeholder="Auto-fill"
                    invalid={!!rowErrors?.brand_id || !!fetchState.error}
                    style={{
                      ...inputStyle,
                      paddingRight: fetchState.loading || hasPopulatedBrand ? '36px' : '12px',
                    }}
                    onChange={(e) => {
                      field.onChange(e);
                      setFetchState({ loading: false, error: null, loaded: false });
                    }}
                  />
                )}
              />
              {fetchState.loading && (
                <div style={inputIconStyle}>
                  <Spinner size="sm" color="secondary" />
                </div>
              )}
              {!fetchState.loading && hasPopulatedBrand && (
                <div style={inputIconStyle}>
                  <Check size={14} color="#28a745" />
                </div>
              )}
            </div>
            {(rowErrors?.brand_id || fetchState.error) && (
              <FormFeedback style={{ display: 'block' }}>
                {rowErrors?.brand_id?.message || fetchState.error}
              </FormFeedback>
            )}
          </FormGroup>
        </Col>

        <Col md={4}>
          <FormGroup>
            <Label style={labelStyle}>
              {nameLabel} {nameRequired && <span style={{ color: '#e53' }}>*</span>}
            </Label>
            <Controller
              name={`${fieldPrefix}.${index}.name`}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  placeholder={namePlaceholder}
                  invalid={!!rowErrors?.name}
                  style={inputStyle}
                  onChange={(e) => {
                    field.onChange(e);
                    setFetchState((prev) => ({ ...prev, loaded: false }));
                  }}
                />
              )}
            />
            {rowErrors?.name && (
              <FormFeedback style={{ display: 'block' }}>
                {rowErrors.name.message}
              </FormFeedback>
            )}
          </FormGroup>
        </Col>

        <Col md={4}>
          <FormGroup>
            <Label style={labelStyle}>
              Website URL {websiteRequired && <span style={{ color: '#e53' }}>*</span>}
            </Label>
            <Controller
              name={`${fieldPrefix}.${index}.website`}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  placeholder="http://example.com"
                  invalid={!!rowErrors?.website}
                  style={inputStyle}
                  onChange={(e) => {
                    field.onChange(e);
                    setFetchState((prev) => ({ ...prev, loaded: false }));
                  }}
                  onBlur={(e) => {
                    field.onBlur(e);
                    onWebsiteBlur?.(index);
                  }}
                />
              )}
            />
            {rowErrors?.website && (
              <FormFeedback style={{ display: 'block' }}>
                {rowErrors.website.message}
              </FormFeedback>
            )}
            {websiteFooter}
          </FormGroup>
        </Col>

        <Col md={1} className="d-flex align-items-center" style={{ paddingTop: '18px' }}>
          <button
            type="button"
            onClick={onRemove}
            style={deleteBtnStyle}
            disabled={!canRemove}
          >
            <X size={28} />
          </button>
        </Col>
      </Row>
    </div>
  );
};

const labelStyle = {
  fontSize: '13px',
  fontWeight: '500',
  color: '#333',
  marginBottom: '6px',
};

const inputStyle = {
  fontSize: '13px',
  borderRadius: '6px',
  border: '1px solid #d9d9d9',
  padding: '10px 12px',
  color: '#333',
};

const inputIconStyle = {
  position: 'absolute',
  right: '10px',
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  alignItems: 'center',
};

const deleteBtnStyle = {
  background: 'black',
  border: 'none',
  cursor: 'pointer',
  color: '#fff',
  width: '25px',
  height: '25px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '43px',
  transition: 'all 0.2s',
};

export default BrandInSpaceRow;
